/**
 * The wetalk app's two portal routes, built here so `router.tsx` gains one entry each (plan-034 WAPP-APPSIGNIN; the
 * precedent of `../confirm-operator-action/confirm-operator-action-route.tsx`).
 *
 *   /app-sign-in                the consent page (`./app-sign-in-screen.tsx`)
 *   /app-link/sign-in/finish    the static return page (`./app-sign-in-finish-screen.tsx`)
 *
 * Both are children of the ROOT, never of the shell: the consent page sends a signed-out visitor through `/log-in`
 * itself (with the values held for the tab, `./app-sign-in-request.ts`), and the return page is reached by a browser
 * that may hold no sign-in at all.
 */
import { createRoute, useLocation, useNavigate, type AnyRoute } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { portalText } from "../../i18n/portal.js";

import { APP_SIGN_IN_FINISH_PATH, AppSignInFinishScreen } from "./app-sign-in-finish-screen.js";
import { appSignInSearchOf, type AppSignInSearch } from "./app-sign-in-request.js";
import { AppSignInScreen } from "./app-sign-in-screen.js";

export function appSignInRouteUnder<TParent extends AnyRoute>(parent: TParent) {
  const route = createRoute({
    getParentRoute: () => parent,
    path: "/app-sign-in",
    staticData: { pageTitle: () => portalText("appSignIn", "page.consent") },
    validateSearch: (raw: Record<string, unknown>): AppSignInSearch => appSignInSearchOf(raw),
    component: AppSignInRoute,
  });
  function AppSignInRoute(): ReactNode {
    return <AppSignInScreen search={route.useSearch()} />;
  }
  return route;
}

/** Takes anything that arrived after the path out of the address bar — without reading it. */
function AppSignInFinishRoute(): ReactNode {
  const carried = useLocation({ select: (location) => location.searchStr !== "" || location.hash !== "" });
  const navigate = useNavigate();
  useEffect(() => {
    if (carried) void navigate({ to: APP_SIGN_IN_FINISH_PATH, search: {}, hash: "", replace: true });
  }, [carried, navigate]);
  return <AppSignInFinishScreen />;
}

export function appSignInFinishRouteUnder<TParent extends AnyRoute>(parent: TParent) {
  return createRoute({
    getParentRoute: () => parent,
    path: APP_SIGN_IN_FINISH_PATH,
    staticData: { pageTitle: () => portalText("appSignIn", "page.finish") },
    // Nothing from the query is kept: the code the address may carry is not this page's to read.
    validateSearch: (): Record<string, never> => ({}),
    component: AppSignInFinishRoute,
  });
}
